(() => {
  'use strict';

  const STORAGE_KEY = 'tf:compare:v1';

  function normalizeHandle(value) {
    const handle = String(value || '')
      .trim()
      .toLowerCase();

    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)
      ? handle
      : '';
  }

  function normalizeText(value, maximumLength = 250) {
    return String(value || '')
      .trim()
      .slice(0, maximumLength);
  }

  function normalizeProductUrl(value, handle) {
    try {
      const url = new URL(
        String(value || ''),
        window.location.origin
      );

      if (url.origin !== window.location.origin) {
        return `/products/${handle}`;
      }

      return `${url.pathname}${url.search}`;
    } catch (error) {
      return `/products/${handle}`;
    }
  }

  function normalizeImageUrl(value) {
    if (!value) return '';

    try {
      const url = new URL(
        String(value),
        window.location.origin
      );

      if (!['http:', 'https:'].includes(url.protocol)) {
        return '';
      }

      return url.href;
    } catch (error) {
      return '';
    }
  }

  function normalizeItem(item) {
    if (!item || typeof item !== 'object') return null;

    const handle = normalizeHandle(item.handle);

    if (!handle) return null;

    return {
        handle,
        title: normalizeText(item.title, 200),
        url: normalizeProductUrl(item.url, handle),
        image: normalizeImageUrl(item.image),
        imageSrcset: normalizeText(item.imageSrcset, 2000),
        imageWidth: Number.parseInt(item.imageWidth, 10) || 200,
        imageHeight: Number.parseInt(item.imageHeight, 10) || 267,
        imageAlt: normalizeText(
            item.imageAlt || item.title,
            250
        )
    };
  }

  function normalizeItems(items) {
    if (!Array.isArray(items)) return [];

    const result = [];
    const usedHandles = new Set();

    items.forEach((item) => {
        const normalizedItem = normalizeItem(item);

        if (
        !normalizedItem ||
        usedHandles.has(normalizedItem.handle)
        ) {
        return;
        }

        usedHandles.add(normalizedItem.handle);
        result.push(normalizedItem);
    });

    return result;
    }

  function getLiveRegion() {
    let liveRegion = document.querySelector(
      '[data-compare-live-region]'
    );

    if (liveRegion) return liveRegion;

    liveRegion = document.createElement('span');
    liveRegion.className = 'visually-hidden';
    liveRegion.dataset.compareLiveRegion = '';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');

    document.body.appendChild(liveRegion);

    return liveRegion;
  }

  function announce(message) {
    if (!message) return;

    const liveRegion = getLiveRegion();

    liveRegion.textContent = '';

    window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  function syncCompareCounts(count) {
    document
      .querySelectorAll('[data-compare-count]')
      .forEach((element) => {
        element.textContent = String(count);
        element.toggleAttribute('hidden', count === 0);
      });
  }

  class CompareStore {
    constructor() {
        this.listeners = new Set();
        this.items = this.read();

        this.handleStorageChange =
            this.handleStorageChange.bind(this);

        window.addEventListener(
            'storage',
            this.handleStorageChange
        );
    }

    read() {
      try {
        const storedValue =
          window.localStorage.getItem(STORAGE_KEY);

        if (!storedValue) return [];

        return normalizeItems(
            JSON.parse(storedValue)
        );
      } catch (error) {
        console.warn(
          '[Compare] Unable to read stored products.',
          error
        );

        return [];
      }
    }

    write() {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(this.items)
        );

        return true;
      } catch (error) {
        console.warn(
          '[Compare] Unable to save products.',
          error
        );

        return false;
      }
    }

    getAll() {
      return this.items.map((item) => ({ ...item }));
    }

    has(handle) {
      const normalizedHandle = normalizeHandle(handle);

      return this.items.some(
        (item) => item.handle === normalizedHandle
      );
    }

    subscribe(callback) {
      if (typeof callback !== 'function') {
        return () => {};
      }

      this.listeners.add(callback);

      return () => {
        this.listeners.delete(callback);
      };
    }

    emit(detail = {}) {
      const payload = {
        action: detail.action || 'change',
        handle: detail.handle || '',
        item: detail.item || null,
        source: detail.source || null,
        items: this.getAll(),
        count: this.items.length
      };

      this.listeners.forEach((listener) => {
        try {
          listener(payload);
        } catch (error) {
          console.error(
            '[Compare] Listener failed.',
            error
          );
        }
      });

      document.dispatchEvent(
        new CustomEvent('compare:change', {
          detail: payload
        })
      );

      syncCompareCounts(payload.count);
    }

    add(item, source = null) {
      const normalizedItem = normalizeItem(item);

      if (!normalizedItem) {
        return {
          action: 'invalid'
        };
      }

      if (this.has(normalizedItem.handle)) {
        return {
          action: 'existing',
          item: normalizedItem
        };
      }


      this.items.push(normalizedItem);
      this.write();

      this.emit({
        action: 'add',
        handle: normalizedItem.handle,
        item: normalizedItem,
        source
      });

      return {
        action: 'add',
        item: normalizedItem
      };
    }

    remove(handle, source = null) {
      const normalizedHandle = normalizeHandle(handle);

      if (!normalizedHandle) {
        return {
          action: 'invalid'
        };
      }

      const removedItem = this.items.find(
        (item) => item.handle === normalizedHandle
      );

      if (!removedItem) {
        return {
          action: 'missing'
        };
      }

      this.items = this.items.filter(
        (item) => item.handle !== normalizedHandle
      );

      this.write();

      this.emit({
        action: 'remove',
        handle: normalizedHandle,
        item: removedItem,
        source
      });

      return {
        action: 'remove',
        item: removedItem
      };
    }

    toggle(item, source = null) {
      const normalizedItem = normalizeItem(item);

      if (!normalizedItem) {
        return {
          action: 'invalid'
        };
      }

      return this.has(normalizedItem.handle)
        ? this.remove(normalizedItem.handle, source)
        : this.add(normalizedItem, source);
    }

    clear(source = null) {
      if (!this.items.length) return;

      this.items = [];
      this.write();

      this.emit({
        action: 'clear',
        source
      });
    }

    handleStorageChange(event) {
      if (event.key !== STORAGE_KEY) return;

      this.items = this.read();

      this.emit({
        action: 'storage'
      });
    }
  }
    const compareStore = new CompareStore();

  class CompareToggle extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleStoreChange =
        this.handleStoreChange.bind(this);

      this.unsubscribeStore = null;
    }

    connectedCallback() {
      this.button = this.querySelector(
        '[data-compare-button]'
      );

      this.tooltip = this.querySelector(
        '[data-compare-tooltip]'
      );

      this.product = normalizeItem({
        handle: this.dataset.productHandle,
        title: this.dataset.productTitle,
        url: this.dataset.productUrl,
        image: this.dataset.productImage,
        imageSrcset: this.dataset.productImageSrcset,
        imageWidth: this.dataset.productImageWidth,
        imageHeight: this.dataset.productImageHeight,
        imageAlt: this.dataset.productImageAlt
    });

      if (!this.button || !this.product) return;

      this.button.removeEventListener(
        'click',
        this.handleClick
      );

      this.button.addEventListener(
        'click',
        this.handleClick
      );

      this.unsubscribeStore?.();

      this.unsubscribeStore = compareStore.subscribe(
        this.handleStoreChange
      );

      this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener(
        'click',
        this.handleClick
      );

      this.unsubscribeStore?.();
      this.unsubscribeStore = null;
    }

    handleClick(event) {
      event.preventDefault();

      const result = compareStore.toggle(
        this.product,
        this
      );

      if (result.action === 'add') {
        announce(this.dataset.addedMessage);
      }

      if (result.action === 'remove') {
        announce(this.dataset.removedMessage);
      }

    }

    handleStoreChange() {
      this.render();
    }

    render() {
      if (!this.button || !this.product) return;

      const isActive = compareStore.has(
        this.product.handle
      );

      const label = isActive
        ? this.dataset.removeLabel
        : this.dataset.addLabel;

      const tooltipText = isActive
        ? this.dataset.removeTooltip
        : this.dataset.addTooltip;

      this.classList.toggle(
        'is-active',
        isActive
      );

      this.button.classList.toggle(
        'is-active',
        isActive
      );

      this.button.setAttribute(
        'aria-pressed',
        isActive ? 'true' : 'false'
      );

      if (label) {
        this.button.setAttribute(
          'aria-label',
          label
        );
      }

      if (this.tooltip && tooltipText) {
        this.tooltip.textContent = tooltipText;
      }
    }
  }

  class CompareDrawer extends HTMLElement {
    constructor() {
      super();

      this.isOpen = false;
      this.lastTrigger = null;
      this.closeTimer = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown =
        this.handleKeydown.bind(this);
      this.handleStoreChange =
        this.handleStoreChange.bind(this);

      this.unsubscribeStore = null;
    }

    connectedCallback() {
      this.panel = this.querySelector(
        '[data-compare-panel]'
      );

      this.list = this.querySelector(
        '[data-compare-list]'
      );

      this.emptyMessage = this.querySelector(
        '[data-compare-empty]'
      );

      this.clearButton = this.querySelector(
        '[data-compare-clear]'
      );

      this.comparePageLink = this.querySelector(
        '[data-compare-page-link]'
      );

      this.abortController?.abort();
      this.abortController = new AbortController();

      this.addEventListener(
        'click',
        this.handleClick,
        {
          signal: this.abortController.signal
        }
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown,
        {
          signal: this.abortController.signal
        }
      );

      this.unsubscribeStore?.();

      this.unsubscribeStore = compareStore.subscribe(
        this.handleStoreChange
      );

      this.render();
    }

    disconnectedCallback() {
      window.clearTimeout(this.closeTimer);
      this.abortController?.abort();

      this.unsubscribeStore?.();
      this.unsubscribeStore = null;

      document.body.classList.remove(
        'compare-drawer-open'
      );
    }

    handleStoreChange(detail) {
      this.render();

      if (
        detail.action === 'add' 
      ) {
        const trigger =
          detail.source?.button ||
          detail.source?.querySelector?.(
            '[data-compare-button]'
          ) ||
          null;

        this.open(trigger);
      }
    }

    handleClick(event) {
      const closeButton = event.target.closest(
        '[data-compare-close]'
      );

      if (closeButton && this.contains(closeButton)) {
        this.close();
        return;
      }

      const removeButton = event.target.closest(
        '[data-compare-remove]'
      );

      if (removeButton && this.contains(removeButton)) {
        compareStore.remove(
          removeButton.dataset.compareRemove,
          this
        );

        return;
      }

      const clearButton = event.target.closest(
        '[data-compare-clear]'
      );

      if (clearButton && this.contains(clearButton)) {
        compareStore.clear(this);

        announce(this.dataset.clearedMessage);
        return;
      }

      if (
        this.comparePageLink &&
        event.target.closest('[data-compare-page-link]') &&
        !compareStore.getAll().length
      ) {
        event.preventDefault();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      this.trapFocus(event);
    }

    trapFocus(event) {
      const focusableElements = [
        ...this.querySelectorAll(
          [
            'a[href]:not([tabindex="-1"])',
            'button:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
      ].filter(
        (element) =>
          !element.hidden &&
          element.offsetParent !== null
      );

      if (!focusableElements.length) {
        event.preventDefault();
        this.panel?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    open(trigger = null) {
      window.clearTimeout(this.closeTimer);

      if (trigger) {
        this.lastTrigger = trigger;
      }

      if (this.isOpen) return;

      this.isOpen = true;
      this.hidden = false;

      document.body.classList.add(
        'compare-drawer-open'
      );

      window.requestAnimationFrame(() => {
        this.classList.add('is-open');

        const closeButton = this.querySelector(
          '.compare-drawer__close'
        );

        closeButton?.focus({
          preventScroll: true
        });
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.classList.remove('is-open');

      document.body.classList.remove(
        'compare-drawer-open'
      );

      this.closeTimer = window.setTimeout(() => {
        if (this.isOpen) return;

        this.hidden = true;

        if (
          this.lastTrigger &&
          document.contains(this.lastTrigger)
        ) {
          this.lastTrigger.focus({
            preventScroll: true
          });
        }

        this.lastTrigger = null;
      }, 300);
    }

    render() {
      if (!this.list) return;

      const items = compareStore.getAll();
      const fragment =
        document.createDocumentFragment();

      items.forEach((item) => {
        fragment.appendChild(
          this.createCompareItem(item)
        );
      });

      this.list.replaceChildren(fragment);

      const isEmpty = items.length === 0;

      if (this.emptyMessage) {
        this.emptyMessage.hidden = !isEmpty;
      }

      if (this.clearButton) {
        this.clearButton.disabled = isEmpty;
      }

      if (this.comparePageLink) {
        this.comparePageLink.classList.toggle(
          'is-disabled',
          isEmpty
        );

        this.comparePageLink.setAttribute(
          'aria-disabled',
          isEmpty ? 'true' : 'false'
        );

        this.comparePageLink.tabIndex =
          isEmpty ? -1 : 0;
      }

      this.classList.toggle(
        'is-empty',
        isEmpty
      );
    }

    createCompareItem(item) {
      const wrapper = document.createElement('div');

      wrapper.className =
        'tf-compare-item file-delete';

      wrapper.setAttribute('role', 'listitem');
      wrapper.dataset.compareHandle = item.handle;

      const link = document.createElement('a');

      link.href = item.url;
      link.className = 'tf-compare-item__link';
      link.setAttribute(
        'aria-label',
        item.title
      );

        if (item.image) {
            const image = document.createElement('img');

            image.className = 'radius-3';
            image.src = item.image;
            image.alt = item.imageAlt || item.title;

            if (item.imageSrcset) {
                image.srcset = item.imageSrcset;
            }

            image.sizes = [
                '(max-width: 375px) 120px',
                '(min-width: 500px) 320px',
                '32vw'
            ].join(', ');

            image.width = item.imageWidth || 200;
            image.height = item.imageHeight || 267;
            image.loading = 'lazy';
            image.decoding = 'async';

            link.appendChild(image);
            } 
        else {
        const placeholder =
          document.createElement('span');

            placeholder.className =
            'tf-compare-item__placeholder';
            placeholder.setAttribute(
            'aria-hidden',
            'true'
            );

        link.appendChild(placeholder);
      }

      const removeButton =
        document.createElement('button');

      removeButton.type = 'button';
      removeButton.className =
        'icon remove tf-compare-item__remove';

      removeButton.dataset.compareRemove =
        item.handle;

      const removeLabelTemplate =
        this.dataset.removeLabelTemplate ||
        'Remove __PRODUCT__';

      removeButton.setAttribute(
        'aria-label',
        removeLabelTemplate.replace(
          '__PRODUCT__',
          item.title
        )
      );

      removeButton.innerHTML = `
        <svg
            class="icon-svg icon-close"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
        >
            <path
            d="M19.5449 17.9541C19.7563 18.1654 19.875 18.452 19.875 18.7509C19.875 19.0498 19.7563 19.3365 19.5449 19.5478C19.3336 19.7592 19.0469 19.8779 18.748 19.8779C18.4492 19.8779 18.1625 19.7592 17.9512 19.5478L11.999 13.5937L6.04492 19.5459C5.83358 19.7573 5.54693 19.876 5.24805 19.876C4.94916 19.876 4.66251 19.7573 4.45117 19.5459C4.23983 19.3346 4.12109 19.0479 4.12109 18.7491C4.12109 18.4502 4.23983 18.1635 4.45117 17.9522L10.4052 12L4.45304 6.04594C4.2417 5.83459 4.12297 5.54795 4.12297 5.24906C4.12297 4.95018 4.2417 4.66353 4.45304 4.45219C4.66439 4.24084 4.95103 4.12211 5.24992 4.12211C5.54881 4.12211 5.83545 4.24084 6.04679 4.45219L11.999 10.4062L17.953 4.45125C18.1644 4.23991 18.451 4.12117 18.7499 4.12117C19.0488 4.12117 19.3354 4.23991 19.5468 4.45125C19.7581 4.66259 19.8769 4.94924 19.8769 5.24812C19.8769 5.54701 19.7581 5.83366 19.5468 6.045L13.5927 12L19.5449 17.9541Z"
            fill="currentColor"
            ></path>
        </svg>
        `;

      wrapper.appendChild(link);
      wrapper.appendChild(removeButton);

      return wrapper;
    }
  }

  if (!customElements.get('compare-toggle')) {
    customElements.define(
      'compare-toggle',
      CompareToggle
    );
  }

  if (!customElements.get('compare-drawer')) {
    customElements.define(
      'compare-drawer',
      CompareDrawer
    );
  }

  syncCompareCounts(
    compareStore.getAll().length
  );

  window.TFCompare = Object.freeze({
    getAll: () => compareStore.getAll(),
    has: (handle) => compareStore.has(handle),
    remove: (handle) =>
      compareStore.remove(handle),
    clear: () => compareStore.clear(),
    subscribe: (callback) =>
      compareStore.subscribe(callback)
  });

  document.dispatchEvent(
    new CustomEvent('compare:ready')
  );
})();